/**
 * Renders the agent's `show_options` payload as a support-prompt exchange, following
 * Meridian's conversation-design guidance: the question sits in an incoming Message
 * bubble (so it reads as the bot asking), and the options render as outlined tertiary
 * suggestion buttons directly beneath it, left-aligned under the bubble. Selecting one
 * sends it back as a user message (confirmation), and the free-text input stays enabled.
 *
 * Streamed payloads stay hidden until complete and safe to select.
 */
import React from 'react';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Message from '@amzn/meridian/message';
import Text from '@amzn/meridian/text';
import { type ChatMessageProps } from './types';
import { getUiToolRenderState } from './ui-tools';

export const OptionsMessage: React.FC<ChatMessageProps> = ({ part, index, isLastMessage, isLoading, onSend }) => {
  if (getUiToolRenderState(part.state, isLastMessage, isLoading) !== 'available') return null;
  const data = (part.input ?? part.output) as { question: string; options: string[] } | undefined;
  if (!data?.question || !Array.isArray(data?.options)) return null;
  const { question, options } = data;
  return (
    <Column key={index} spacing="300" width="100%">
      <Message maxWidth="300px" showAvatar={false} showMetadata={false} size="medium" type="incoming">
        <Text breakWord color="primary" tag="div" type="b300">{question}</Text>
      </Message>
      <div data-testid="chat-options" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        {options.map((opt: string) => (
          <Button disabled={!isLastMessage || isLoading} key={opt} size="small" type="tertiary" onClick={() => { onSend(opt); }}>
            {opt}
          </Button>
        ))}
      </div>
    </Column>
  );
};
